import { useMutation } from '@tanstack/react-query';
import { postChangePassword } from '../../infrastructure/api/usersApi';
import type { PasswordChangeDto } from '../../domain/contracts/api.contracts';
import { useAuthStore } from '../stores/authStore';

/** Codes the backend puts in the problem-details `code` field for E33 (API_CONTRACT §1.4). */
export type ChangePasswordErrorCode =
  | 'VALIDATION_FAILED'
  | 'CURRENT_PASSWORD_INVALID'
  | 'NEW_PASSWORD_SAME_AS_CURRENT'
  | 'PASSWORD_POLICY'
  | 'RATE_LIMITED'
  | 'ACCOUNT_SUSPENDED'
  | 'STALE_UPDATE';

export interface ChangePasswordFailure {
  /** Undefined = no response at all (offline, DNS, timeout). */
  status?: number;
  code?: ChangePasswordErrorCode;
  /** From the problem body, falling back to the `Retry-After` header. */
  retryAfterSeconds?: number;
}

/** Reads the typed failure out of an axios error. Never touches the request body, so no password is echoed. */
export function changePasswordFailureOf(error: unknown): ChangePasswordFailure {
  const res = (error as { response?: { status?: number; data?: Record<string, unknown>; headers?: Record<string, unknown> } })
    ?.response;
  if (!res) return {};
  const data = res.data ?? {};
  const header = Number(res.headers?.['retry-after']);
  const body = typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : undefined;
  return {
    status: res.status,
    code: typeof data.code === 'string' ? (data.code as ChangePasswordErrorCode) : undefined,
    retryAfterSeconds: body ?? (Number.isFinite(header) && header > 0 ? header : undefined),
  };
}

/**
 * E33 — change the signed-in member's password. On success the access token in the response replaces the in-memory
 * one (the backend bumped the token version, so the old token is dead); the refresh cookie and session carry on.
 * Passwords live only in the mutation call — never in storage, a query key or a log.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: PasswordChangeDto) => postChangePassword(body),
    onSuccess: (res) => useAuthStore.getState().setAccessToken(res.accessToken),
  });
}
